import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWheelSpins, useWheelSpinsSummary } from '@/api/hooks';
import type { WheelSpinsFilters } from '@/api/types';
import { formatCount } from '@/lib/format';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Loading } from '@/ui/Loading';
import { SpinsTable } from './SpinsTable';
import { WheelFilters } from './WheelFilters';
import { PrizeBreakdown, WheelStats } from './WheelSummary';
import { activeFilterCount, filterOptions, filtersFromParams, NO_FILTERS, paramsFromFilters } from './wheel-filters';

export function WheelSpinsPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const setFilters = (next: WheelSpinsFilters) => setParams(paramsFromFilters(next));

  const query = useWheelSpins(filters);
  const summary = useWheelSpinsSummary(filters);
  // Unfiltered, for the dropdown options: every wheel, prize and currency the ledger holds.
  const everything = useWheelSpinsSummary(NO_FILTERS);
  const options = useMemo(() => filterOptions(everything.data, filters.wheelUid), [everything.data, filters.wheelUid]);

  const spins = query.data?.pages.flatMap((page) => page.items) ?? [];
  const active = activeFilterCount(filters);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Daily wheel spins</h1>
        <p className="text-sm text-slate-600">
          Every spin recorded in the ledger, newest first. One spin per qualifying deposit; each tier once per UTC day.
        </p>
      </div>

      <Card
        title="Filters"
        actions={
          active > 0 && (
            <Button type="button" variant="secondary" onClick={() => setFilters(NO_FILTERS)}>
              Clear all ({active})
            </Button>
          )
        }
      >
        <WheelFilters filters={filters} options={options} onChange={setFilters} />
      </Card>

      {summary.isError && <ErrorNotice error={summary.error} />}
      {summary.data && (
        <>
          <WheelStats summary={summary.data} />
          <PrizeBreakdown summary={summary.data} onPick={(prize) => setFilters({ ...filters, ...prize })} />
        </>
      )}

      <Card
        title="Spins"
        description={
          query.data
            ? summary.data
              ? `${formatCount(spins.length)} of ${formatCount(summary.data.spins)} shown`
              : `${formatCount(spins.length)} loaded`
            : undefined
        }
      >
        {query.isPending && <Loading what="spins" />}
        {query.isError && <ErrorNotice error={query.error} />}
        {query.data && (
          <>
            <SpinsTable spins={spins} onPickPlayer={(playerUuid) => setFilters({ ...filters, playerUuid })} />
            {query.hasNextPage && (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  onClick={() => void query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  {query.isFetchingNextPage ? 'Loading…' : 'Load older spins'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
